import { useMutation, useQuery } from "@tanstack/react-query";
import { managerEndpoints, sharedEndpoints } from "@src/services/endpoints";
import SignolUtils from "@src/utils/signolUtils";
import { trackError } from "../../utils/userActivityTracker";

const MILLISECONDS_IN_TEN_MINUTES = 10 * 60 * 1000;

/** ManagerContainer inner components will rely on the QueryClient CACHE (staled data) as primary source of state-sync */
const ManagerHooks = {
  useGetVehicles: (role) =>
    useQuery({
      queryKey: ["VEHICLES"],
      queryFn: () => managerEndpoints.fetchVehicles(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /** @type {{ vehicleTypes: string[], vehicleIds: string[], vehicleCount: number }} */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          vehicleTypes: data.vehicleTypes,
          vehicleList: data.vehicleIds,
          numberOfVehicles: data.vehicleCount,
        };
      },
    }),
  useGetRoutes: (role) =>
    useQuery({
      queryKey: ["ROUTES"],
      queryFn: () => managerEndpoints.fetchRoutes(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /** @type {{ routes: Array<{ origin: string, destination: string }> }} */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          routes: data.routes,
        };
      },
    }),
  useGetFeedbackSearchResult: (role, startDate, endDate, body) =>
    useQuery({
      queryKey: ["FEEDBACK", role, startDate, endDate, body],
      queryFn: () =>
        managerEndpoints.fetchFeedbackSearchResults(
          null,
          startDate,
          endDate,
          body,
        ),
      enabled: () => SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {{
         *  commentCount: number,
         *  comments: Array<{comment: string, eventOccurrenceMonth: number, behaviourName: string, behaviourAcronym: string}>
         * }}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          comments: data.comments,
          commentCount: data.commentCount,
          cdata: data,
        };
      },
    }),
  useGetNotificationsPerTemplatePerDay: (role, teamId, startDate, endDate) =>
    useQuery({
      queryKey: ["NOTIFICATIONS_PER_TEMPLATE", teamId, startDate, endDate],
      queryFn: () =>
        managerEndpoints.fetchNotificationsPerTemplatePerDay(
          teamId,
          startDate === "all" ? undefined : startDate,
          endDate,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          /**
           * @type {Array<{
           *   day: number,
           *   totalSent: number,
           *   notifications: Array<{templateName: string, countSent: number}>
           * }>}
           */
          notificationsPerTemplatePerDay: data.calendar,
        };
      },
    }),
  useGetLatestDataReceived: (role) =>
    useQuery({
      queryKey: ["LATEST_DATA_RECEIVED", role],
      queryFn: () => managerEndpoints.fetchLatestDataReceived(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          /** @type {Date} */
          latestDataReceivedTimestamp: data.latestDataReceivedTimestamp,
        };
      },
    }),
  useGetEventTotals: (role) =>
    useQuery({
      queryKey: ["STATS_EVENT_TOTALS", role],
      queryFn: () => managerEndpoints.fetchEventTotals(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          countEventsPreSignol: data.countEvents.preSignol,
          countEventsPostSignol: data.countEvents.postSignol,
          countOperators: data.countOperators,
          countOperatorUsers: data.countOperatorUsers,
        };
      },
    }),
  useGetBehaviourSuccessVsPreSignol: (role, teamId, startDate, endDate) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_SUCCESS_RATE_VS_PRE_SIGNOL",
        role,
        teamId,
        startDate,
        endDate,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourSuccessRateVsPreSignol(
          teamId,
          startDate === "all" ? undefined : startDate,
          endDate,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {Array<{
         *   behaviourKey: string,
         *   behaviourName: string,
         *   behaviourAcronym: string,
         *   behaviourPosition: number,
         *   countEventsPerformablePreSignol: number,
         *   countEventsPerformablePostSignol: number,
         *   countEventsPerformedPreSignol: number,
         *   countEventsPerformedPostSignol: number,
         *   avgSuccessRatePostSignolWeighted: { value: number, standardError95: number },
         *   avgSuccessRatePreSignolWeighted: { value: number, standardError95: number },
         *   avgSuccessRateChange: { value: number, standardError95: number }
         * }>}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        data.sort((a, b) => a.behaviourPosition - b.behaviourPosition);
        return data;
      },
    }),
  useGetBehaviourSuccessOverTime: (role, behaviourKey, teamId) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_SUCCESS_RATE_OVER_TIME",
        role,
        teamId,
        behaviourKey,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourSuccessRateOverTime(
          behaviourKey,
          teamId,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {{
         *   baseline: { value: number, standardError95: number },
         *   periods: Array<{
         *     timestamp: number,
         *     countEventsPerformable: number,
         *     countEventsPerformed: number,
         *     value: { value: number, standardError95: number },
         *     valueChangeFromBaseline: { value: number, standardError95: number }
         *   }>
         * }}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          baseline: data.baseline,
          periods: data.periods,
        };
      },
    }),
  useGetBehaviourSuccessPerOperator: (role, behaviourKey, teamId) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_SUCCESS_RATE_PER_OPERATOR",
        role,
        teamId,
        behaviourKey,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourSuccessRatePerOperator(
          behaviourKey,
          teamId,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {Array<{
         *   countEventsPerformablePostSignol: number,
         *   countEventsPerformedPostSignol: number,
         *   successRateActual: number,
         *   successRatePreSignol: number,
         * }>}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.map((d) => ({
          ...d,
          betterThanPreSignol: d.successRateActual > d.successRatePreSignol,
        }));
      },
    }),
  useGetBehaviourFuelSavingsOverTime: (role, behaviourKey, teamId) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_FUEL_SAVINGS_OVER_TIME",
        role,
        teamId,
        behaviourKey,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourFuelSavingsOverTime(
          behaviourKey,
          teamId,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {{
         *   baseline: { value: number, standardError95: number },
         *   periods: Array<{
         *     timestamp: number,
         *     countEventsPerformable: number,
         *     countEventsPerformed: number,
         *     value: { value: number, standardError95: number },
         *     valueChangeFromBaseline: { value: number, standardError95: number }
         *   }>
         * }}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          baseline: data.baseline,
          periods: data.periods,
        };
      },
    }),
  useGetBehaviourFuelSavingsPerOperator: (role, behaviourKey, teamId) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_FUEL_SAVINGS_PER_OPERATOR",
        role,
        teamId,
        behaviourKey,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourFuelSavingsPerOperator(
          behaviourKey,
          teamId,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {Array<{
         *   countEventsPerformablePostSignol: number,
         *   countEventsPerformedPostSignol: number,
         *   avgNetFuelSavingKgPreSignol: number,
         *   avgNetFuelSavingKgPostSignol: number,
         * }>}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return data.map((d) => ({
          ...d,
          betterThanPreSignol:
            d.avgNetFuelSavingKgPostSignol > d.avgNetFuelSavingKgPreSignol,
        }));
      },
    }),
  useGetBehaviourFuelSavingsVsPreSignol: (role, teamId, startDate, endDate) =>
    useQuery({
      queryKey: [
        "BEHAVIOURS_FUEL_SAVINGS_VS_PRE_SIGNOL",
        role,
        teamId,
        startDate,
        endDate,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourFuelSavingVsPreSignol(
          teamId,
          startDate === "all" ? undefined : startDate,
          endDate,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {Array<{
         *   behaviourKey: string,
         *   behaviourName: string,
         *   behaviourAcronym: string,
         *   behaviourPosition: number,
         *   countEventsPerformedPreSignol: number,
         *   countEventsPerformedPostSignol: number,
         *   totalFuelSavingKgPreSignol: number,
         *   totalFuelSavingKgPostSignol: number,
         *   avgNetFuelSavingKgPreSignol: { value: number, standardError95: number },
         *   avgNetFuelSavingKgPostSignol: { value: number, standardError95: number },
         *   avgNetFuelSavingChangeKg: { value: number, standardError95: number },
         * }>}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        data.sort((a, b) => a.behaviourPosition - b.behaviourPosition);
        return data;
      },
    }),
  useGetNotificationTotals: (role) =>
    useQuery({
      queryKey: ["NOTIFICATION_TOTAL_PER_TYPE", role],
      queryFn: () => managerEndpoints.fetchNotificationTotals(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {{
         *   notificationTotals: Array<{
         *     type: string;
         *     countTotal: number;
         *     countDelivered: number;
         *     countSeen: number;
         *     rateSeen: number;
         *   }>;
         * }}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        const emailNotificationTotals = data.notificationTotals.filter(
          (n) => n.type === "EMAIL",
        )?.[0];
        return {
          notificationTotals: emailNotificationTotals,
        };
      },
    }),
  useGetQuickSightDashboardUrl: (role, behaviourAcronym) =>
    useQuery({
      queryKey: ["QUICKSIGHT_DASHBOARD_URL", role, behaviourAcronym],
      queryFn: () => managerEndpoints.fetchQuickSightDashboardUrl(),
      enabled: SignolUtils.isManager(role),
      staleTime: 0, //url is one-time use
      select: (data) => {
        if (data.result === false) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          /** @type {string} */
          dashboardEmbedUrl: data.data.dashboardEmbedUrl,
        };
      },
    }),
  useGetBehaviourExclusions: (role) =>
    useQuery({
      queryKey: ["BEHAVIOUR_EXCLUSIONS", role],
      queryFn: () => sharedEndpoints.fetchBvrExclusions(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /** @type {Record<string, Array<{field_name: string, values: string[]}>} */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          exclusions: data,
        };
      },
    }),
  useGetPageViews: (role) =>
    useQuery({
      queryKey: ["PAGE_VIEWS", role],
      queryFn: () => managerEndpoints.fetchPageViews(),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          countPageViews: data.countPageViews,
          /** @type {Array<{count: number, url: string}>} */
          pageViews: data.pageViews,
        };
      },
    }),
  useGetBehaviourDataOverviewStatusCounts: (
    role,
    behaviourKey,
    teamId,
    startDate,
    endDate,
  ) =>
    useQuery({
      queryKey: [
        "DATA_OVERVIEW_STATUS_COUNTS",
        role,
        behaviourKey,
        teamId,
        startDate,
        endDate,
      ],
      queryFn: () =>
        managerEndpoints.fetchBehaviourDataOverviewStatusCounts(
          behaviourKey,
          teamId,
          startDate === "all" ? undefined : startDate,
          endDate,
        ),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /**
         * @type {{
         *   behaviourStatusCounts: {
         *       countSuccess: number;
         *       countExcluded: number;
         *       countUnsuccessful: number;
         *       countBadMissingData: number;
         *   };
         *   countBehavioursTotal: number;
         *   countBehavioursPerformable: number;
         * }}
         */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          behaviourStatusCounts: data.behaviourStatusCounts,
          countBehavioursTotal: data.countBehavioursTotal,
          countBehavioursPerformable: data.countBehavioursPerformable,
        };
      },
    }),
  useSubmitSupportRequest: (callback) =>
    useMutation({
      mutationFn: (inputData) => sharedEndpoints.sendSupportRequest(inputData),
      onSuccess: (data) => {
        console.log("Support request sent successfully", data);
        // handle success, e.g., show a toast or update state
        if (callback) {
          callback(true);
        }
      },
      onError: (error, variables, context) => {
        console.error("Error sending support request", error);
        if (callback) {
          callback(false);
        }
        trackError(error, "operator-SupportRequest", { variables, context });
        // handle error, e.g., show an error message
      },
    }),

  useExecuteDataQuery: (role, sqlQuery, enabled = false, mode = "basic") =>
    useQuery({
      queryKey: ["DATA_QUERY", sqlQuery, mode],
      queryFn: () => managerEndpoints.executeDataQuery(mode, sqlQuery),
      enabled: SignolUtils.isManager(role) && enabled && !!sqlQuery,
      staleTime: 0, // Don't cache query results
      select: (data) => {
        if (data.error) {
          throw Error(data.message || data.error);
        }
        return {
          total_count: data.row_count,
          schema: data.schema || [],
          rows: (data.rows || []).slice(0, 100),
        };
      },
    }),

  useDownloadDataQuery: (role) =>
    useMutation({
      mutationFn: (query) => managerEndpoints.downloadDataQuery(query),
      onError: (error) => {
        console.error("Error generating download link", error);
        trackError(error, "manager-DataQueryDownload");
      },
    }),

  useGetPlotlyBVRGraphs: (keys, role) =>
    useQuery({
      queryKey: ["PLOTLY_BVR_GRAPHS", keys],
      queryFn: () => managerEndpoints.fetchPlotlyGraphs(keys),
      enabled: SignolUtils.isManager(role),
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (data) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        if (data.errors?.length) {
          console.warn("missing graph keys:", data.errors);
        }
        return {
          graphs: data.behiveOutputs ?? [],
          missingKeys: data.errors ?? [],
        };
      },
    }),
  // to invalidate cache, please see more: https://tanstack.com/query/v4/docs/framework/react/guides/invalidations-from-mutations
};

export default ManagerHooks;
